import { API_CONFIG } from '@/config/api'

/**
 * Resolving recordings to a directly-playable URL.
 *
 * Historically the app fetched every recording through the authenticated
 * `/api/recordings/media/<path>` endpoint (bearer header -> blob -> object URL),
 * which streamed the whole file through the single ASGI worker and blocked every
 * other request while a large `.webm` downloaded — the root cause of the
 * intermittent 502s.
 *
 * Instead we ask `/api/recordings/media-url/<path>` (a small JSON call that keeps
 * the same permission checks) for a URL the browser can load directly. On S3 the
 * backend returns a short-lived presigned URL, so the bytes flow straight from
 * S3 into the `<video>` element. With local filesystem storage the backend flags
 * `stream`, and we fall back to the legacy authenticated fetch -> blob URL.
 *
 * Note: a presigned URL must be assigned straight to `<video src>`; it cannot be
 * reached via a bearer-authenticated `fetch()` because that request is
 * preflighted and the browser refuses to follow the cross-origin redirect.
 */

function httpsAwareApiOrigin(): string {
  const base = API_CONFIG.BASE_URL.trim()
  let origin = new URL(base).origin
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    origin.startsWith('http:')
  ) {
    origin = origin.replace(/^http:/i, 'https:')
  }
  return origin
}

/**
 * Extract the `recordings/...` storage key from any recording reference: an
 * `/api/recordings/media/<key>` URL, a stored `/media/<key>` (or S3) URL, or a
 * bare key. Returns null when no key can be derived.
 */
export function mediaKeyFromUrl(sourceUrl: string): string | null {
  let pathname = sourceUrl
  try {
    pathname = new URL(sourceUrl, httpsAwareApiOrigin()).pathname
  } catch {
    // sourceUrl is already a path or bare key — fall through with it as-is.
  }
  try {
    pathname = decodeURIComponent(pathname)
  } catch {
    // leave pathname undecoded if it isn't valid percent-encoding
  }

  const apiMarker = '/api/recordings/media/'
  if (pathname.includes(apiMarker)) {
    return pathname.slice(pathname.indexOf(apiMarker) + apiMarker.length)
  }
  const mediaMarker = '/media/'
  if (pathname.includes(mediaMarker)) {
    return pathname.slice(pathname.indexOf(mediaMarker) + mediaMarker.length)
  }
  const recIdx = pathname.indexOf('recordings/')
  if (recIdx >= 0) {
    return pathname.slice(recIdx)
  }
  return null
}

/**
 * Resolve a recording reference to a URL ready for `<video src>`.
 *
 * Returns a presigned S3 URL (S3 storage) or a blob: object URL (local storage
 * fallback). Throws if the key can't be derived or the request fails.
 */
export async function resolvePlayableVideo(sourceUrl: string, token: string): Promise<string> {
  const key = mediaKeyFromUrl(sourceUrl)
  if (!key) {
    throw new Error('Could not resolve recording media key')
  }

  const endpoint = `${httpsAwareApiOrigin()}/api/recordings/media-url/${key}`
  const res = await fetch(endpoint, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    throw new Error(`Failed to resolve video URL: ${res.status} ${res.statusText}`)
  }

  const data = (await res.json()) as { url: string; stream?: boolean }

  if (data.stream) {
    // Local filesystem storage: no presigning, so stream through the app with
    // the bearer token and wrap the bytes in an object URL (legacy behaviour).
    const blobRes = await fetch(data.url, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!blobRes.ok) {
      throw new Error(`Failed to load video: ${blobRes.status} ${blobRes.statusText}`)
    }
    return URL.createObjectURL(await blobRes.blob())
  }

  return data.url
}
